import { useEffect, useRef, useState, type FormEvent, type KeyboardEvent } from 'react';

import { SendHorizontal, Square } from 'lucide-react';

import { cn } from '../utils/cn';

const MAX_CHARS = 500;

interface ChatComposerProps {
    isStreaming: boolean;
    onSend: (text: string) => void;
    onStop: () => void;
}

export const ChatComposer = ({ isStreaming, onSend, onStop }: ChatComposerProps) => {
    const [value, setValue] = useState('');
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    useEffect(() => {
        const el = textareaRef.current;
        if (!el) return;
        el.style.height = 'auto';
        el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
    }, [value]);

    useEffect(() => {
        if (!isStreaming) textareaRef.current?.focus();
    }, [isStreaming]);

    const submit = () => {
        const text = value.trim();
        if (!text || isStreaming) return;
        onSend(text);
        setValue('');
    };

    const handleSubmit = (e: FormEvent) => {
        e.preventDefault();
        submit();
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
            e.preventDefault();
            submit();
        }
    };

    const canSend = value.trim().length > 0 && !isStreaming;

    return (
        <form
            onSubmit={handleSubmit}
            className="border-t border-slate-200 dark:border-slate-800 bg-white/80 dark:bg-slate-900/80 backdrop-blur p-3 sm:p-4"
        >
            <div className="flex items-end gap-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-950 px-3 py-2 transition-all focus-within:border-emerald-500 focus-within:ring-2 focus-within:ring-emerald-200 dark:focus-within:ring-emerald-900/50">
                <textarea
                    ref={textareaRef}
                    rows={1}
                    value={value}
                    maxLength={MAX_CHARS}
                    onChange={(e) => setValue(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Ask a financial question…"
                    aria-label="Message the wealth-building assistant"
                    className="max-h-40 flex-1 resize-none bg-transparent py-1.5 text-sm text-slate-800 dark:text-slate-100 placeholder:text-slate-400 outline-none"
                />
                {isStreaming ? (
                    <button
                        type="button"
                        onClick={onStop}
                        className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-slate-800 dark:bg-slate-700 px-3 text-xs font-bold text-white transition-colors hover:bg-slate-900 dark:hover:bg-slate-600"
                        aria-label="Stop generating"
                    >
                        <Square className="h-3.5 w-3.5 fill-current" /> Stop
                    </button>
                ) : (
                    <button
                        type="submit"
                        disabled={!canSend}
                        className={cn(
                            'inline-flex h-9 items-center gap-1.5 rounded-lg px-3 text-xs font-bold text-white transition-colors',
                            'bg-emerald-600 hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-40',
                        )}
                        aria-label="Send message"
                    >
                        <SendHorizontal className="h-4 w-4" /> Send
                    </button>
                )}
            </div>
            <div className="mt-1.5 flex justify-between px-1 text-[10px] text-slate-400">
                <span>
                    <kbd className="font-mono">Enter</kbd> to send · <kbd className="font-mono">Shift+Enter</kbd> for a new line
                </span>
                <span className={cn(value.length > MAX_CHARS * 0.9 && 'text-amber-500')}>
                    {value.length}/{MAX_CHARS}
                </span>
            </div>
        </form>
    );
};
